import React, { useState, useMemo } from "react"
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom"
import { ThemeProvider, createTheme } from "@mui/material/styles"
import CssBaseline from "@mui/material/CssBaseline"
import { useMediaQuery, CircularProgress, Box } from "@mui/material"
import Login from "./components/Login"
import Directory from "./components/Directory"
import { AuthProvider, useAuth } from "./context/AuthContext"

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth()
  
  // If still checking Firebase state, show a clean loading screen
  if (loading) {
    return (
      <Box 
        sx={{ 
          display: "flex", 
          justifyContent: "center", 
          alignItems: "center", 
          height: "100vh",
          bgcolor: "background.default"
        }}
      >
        <CircularProgress />
      </Box>
    )
  }
  
  // If no user is authenticated, send to login
  if (!user) {
    return <Navigate to="/login" replace />
  }
  return children
}

const App = () => {
  // Use system preference as default
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)")
  const [mode, setMode] = useState(prefersDarkMode ? "dark" : "light")

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            main: "#1976d2"
          },
          secondary: {
            main: "#dc004e"
          },
          background: {
            default: mode === "light" ? "#f5f5f5" : "#121212",
            paper: mode === "light" ? "#fff" : "#1e1e1e"
          }
        }
      }),
    [mode]
  )

  const toggleColorMode = () => {
    setMode(prevMode => (prevMode === "light" ? "dark" : "light"))
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Directory toggleColorMode={toggleColorMode} mode={mode} />
                </ProtectedRoute>
              }
            />
          </Routes>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  )
}

export default App
